/* eslint-disable promise/prefer-await-to-then */
/* eslint-disable @typescript-eslint/no-invalid-void-type */

import { EActions } from '@/core/types';
import { Logger } from '@/shared/logger/logger';

import { handleConsent } from '../actions/consent';
import { handleEvent } from '../actions/event';
import { handleInit } from '../actions/init';
import { DataLayerStateHandler } from './state';

// Events that should be processed after initialization
const ProcessibleInitActions = new Set([EActions.Event, EActions.Consent]);

/**
 * Mable's Custom Datalayer Implementation
 */
export class CustomDataLayer extends Array<any[]> {
  listeners: Record<
    EActions,
    (...args: any[]) => Promise<any | boolean | void>
  > = {
    [EActions.Consent]: handleConsent,
    [EActions.Event]: handleEvent,
    [EActions.Init]: handleInit,
  };

  /**
   * Handle's Push into the datalayer -> This is a custom implementation
   * Rejects anything that is not in proper format!
   */
  // @ts-expect-error push is actually supposed to be non async but it can work as async
  async push(...args: any[][]): Promise<number> {
    const processedArgs = Promise.all(
      args.map(async arg => {
        // If it is not an array eject it
        if (!Array.isArray(arg)) {
          Logger.info('Not an array', arg);
          return;
        }
        const maybeEventState = arg.at(-1);
        // If event is processed already, then continue to the next event
        if (maybeEventState?.processed) return arg;
        if (maybeEventState?.processed !== undefined) arg.pop();
        // Handle the actions
        const isHandled = await this._triggerAction(...arg);

        // Provides key information about whether it was processed or not
        arg.push({ processed: isHandled });
        return arg;
        // Filter out the invalid ones
      }),
    );

    // Once Processed add the arrays
    return processedArgs.then(res => {
      res = res.filter(arg => arg !== undefined);
      // Finally Adds the arrays
      return Array.prototype.push.apply(this, res);
    }) as unknown as number;
  }

  private async _triggerAction(...args: any[]) {
    const action = args[0] as EActions;
    const params = args.slice(1);

    // If the action is not permissible, return false
    if (!DataLayerStateHandler.isActionPermissible(action)) {
      // Logger.info('Action not permissible', action);
      return false;
    }
    try {
      // Logger.info('Triggering Action', { action, params });
      return await this.listeners[action](...params);
    } catch (error) {
      Logger.error(`Error in Triggering Action ${action}`, error);
      return false;
    }
  }

  /**
   * This is a Special function that is called after initialization to process the complete datalayer
   * This returns a 1:1 copy of Actions added in the datalayer with whether the events are processed or not
   * @param events
   */
  public async processCompleteDataLayer() {
    // if can't do event processing no point of doing this
    if (
      !DataLayerStateHandler.isActionPermissible(EActions.Event) ||
      !DataLayerStateHandler.isActionPermissible(EActions.Consent)
    ) {
      Logger.warn('Event processing not permissible', EActions.Event);
      return false;
    }
    // Logger.info('Processing Complete DataLayer', this);
    // Marking Events as processed to ensure we are good!
    for (let i = 0; i < this.length; i++) {
      const event = this[i];
      // Filter out if not a proper event
      if (!Array.isArray(event)) continue;

      const maybeEventState = event.at(-1);
      // If event is processed already, then continue to the next event
      if (maybeEventState?.processed) continue;
      if (maybeEventState?.processed !== undefined) event.pop();

      const action = event[0] as EActions;

      // If Action is not an event init processible action continue to the next event
      if (!ProcessibleInitActions.has(action)) continue;

      // Handle the events
      const params = event.slice(1);
      // Logger.info('Processing Event', { action, params });
      const res = await this._triggerAction(action, ...params);
      if (res) {
        event.push({ processed: res });
      }
      // Mark the event as processed
      // Logger.info('Event Processed', { action, res });
    }
    return true;
  }
  setListener(
    action: EActions,
    listener: (
      event: string,
      data: Record<string, unknown>,
    ) => Promise<boolean>,
  ): void {
    this.listeners[action] = listener;
  }
}
